import { Check, History, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { GameCode } from '../../entities/room/types';
import { gameNameKey, pickLabel } from '../../shared/game/betOptions';
import { signedNet } from '../../shared/game/betOutcomeText';
import { GameIcon } from '../../shared/game/GameIcon';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtCurrency } from '../../shared/lib/format';
import { buildResultCard, heroDrawFor, type CardBet, type CardDraw, type DrawTag, type HeroDraw, type ResultRow } from '../../shared/room/resultCardView';
import { useAutoClose } from '../../shared/room/useAutoClose';
import { diceFacePips } from '../archive/rulesExample';
import { CelebrationBurst } from './CelebrationBurst';

type T = ReturnType<typeof useI18nStore.getState>['t'];

function tagLabel(tag: DrawTag, t: T): string {
  return 'text' in tag ? tag.text : t(tag.key);
}

/** "1 · Small · Odd" / "1+1+3=5 · Small · Odd" / "21 · Red · Odd · 19–36" */
export function drawValueText(hero: HeroDraw, t: T): string {
  const head = hero.game === 'lastdigit' ? String(hero.digit)
    : hero.game === 'sicbo' ? `${hero.dice.join('+')}=${hero.sum}`
      : String(hero.pocket);
  return [head, ...hero.tags.map((tag) => tagLabel(tag, t))].join(' · ');
}

function DiceFace({ n, size = 22 }: { n: number; size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="3.5" fill="#fef3c7" stroke="#78350f" strokeWidth="0.6" />
      {diceFacePips(n).map(([cx, cy], index) => <circle key={index} cx={cx} cy={cy} r="1.7" fill="#1c1917" />)}
    </svg>
  );
}

function heroValue(hero: HeroDraw): string {
  if (hero.game === 'lastdigit') return String(hero.digit);
  if (hero.game === 'sicbo') return `${hero.dice.join('+')}=${hero.sum}`;
  return String(hero.pocket);
}

function DrawTile({ hero, t }: { hero: HeroDraw; t: T }) {
  return (
    <div className="rr-hero-draw" data-game={hero.game}>
      {hero.game === 'lastdigit' ? <span className="rr-draw-value">{hero.digit}</span> : null}
      {hero.game === 'sicbo' ? (
        <span className="rr-draw-dice">
          {hero.dice.map((value, index) => <DiceFace key={index} n={value} />)}
          <em className="rr-draw-sum">= {hero.sum}</em>
        </span>
      ) : null}
      {hero.game === 'roulette' ? <span className={`rr-draw-pocket tone-${hero.tone}`}>{hero.pocket}</span> : null}
      <span className="rr-tags">
        {hero.tags.map((tag, index) => (
          <b key={index} className={`rr-tag${'key' in tag ? ` tag-${tag.key}` : ''}`}>{tagLabel(tag, t)}</b>
        ))}
      </span>
    </div>
  );
}

export function RoundResultCard({
  stage, roundLabel, roomGame, draw, bets, fiatCurrency, autoClose, onClose, onHistory,
}: {
  stage: 'settling' | 'settled';
  roundLabel: string;
  roomGame: GameCode;
  draw: CardDraw | null;
  bets: CardBet[];
  fiatCurrency: string;
  autoClose: boolean;
  onClose: () => void;
  onHistory: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const [expanded, setExpanded] = useState(false);
  const [burstDone, setBurstDone] = useState(false);
  const view = useMemo(() => buildResultCard({ bets, draw, roomGame, fiatCurrency, expanded }), [bets, draw, roomGame, fiatCurrency, expanded]);
  const settled = stage === 'settled';
  const layout = settled ? view.layout : 'loss';
  const hero = view.hero ?? heroDrawFor(roomGame, draw);
  const { remainingMs, rearm } = useAutoClose({ active: settled && autoClose, durationMs: view.autoCloseMs, onClose });
  const secondsLeft = Math.max(0, Math.ceil(remainingMs / 1000));
  const showBurst = settled && !burstDone && (view.celebration === 'win' || view.celebration === 'mega');
  const title = t('summaryTitle').replace('%round%', roundLabel);
  const amountText = (net: number, currency: string) => signedNet(net, (abs) => fmtCurrency(abs, currency));
  const rows: ResultRow[] = settled
    ? view.rows
    : bets.map((bet) => ({ id: bet.id, game: bet.game, tab: bet.tab, pick: bet.pick, currency: bet.currency, result: 'pending' as const, net: 0 }));

  return (
    <div className="rr-backdrop" role="presentation" onMouseDown={onClose} data-round-result-backdrop="true">
      <section
        className={`rr-card${view.celebration === 'mega' ? ' is-mega' : ''}`}
        data-round-result-card="true"
        data-stage={stage}
        data-layout={layout}
        data-celebration={settled ? view.celebration : 'none'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={rearm}
      >
        <header className="rr-head">
          <h2>{title}</h2>
          <span className="rr-head-actions">
            {settled && autoClose ? <span className="rr-countdown" data-seconds-left={secondsLeft}>{t('summaryAutoClose').replace('%s%', String(secondsLeft))}</span> : null}
            <button className="icon-button" type="button" onClick={onClose} aria-label={t('summaryClose')}>
              <X size={18} aria-hidden="true" />
            </button>
          </span>
        </header>

        {settled && layout === 'win' ? (
          <div className="rr-hero">
            <div>
              {view.nets.map((item) => (
                <strong key={item.currency} className="rr-hero-amount">{amountText(item.net, item.currency)}</strong>
              ))}
              <span className="rr-hero-caption">{t('summaryYouWon')}</span>
            </div>
            {hero ? <DrawTile hero={hero} t={t} /> : null}
          </div>
        ) : (
          <div className="rr-hero">
            {hero ? <DrawTile hero={hero} t={t} /> : null}
            <div>
              <div className="rr-hero-headline">
                {hero
                  ? t('summaryDrawHeadline').replace('%round%', roundLabel).replace('%value%', drawValueText(hero, t))
                  : title}
              </div>
              {!settled ? <span className="rr-hero-caption">{t('summarySettling')}</span> : null}
              {settled && view.breakEven ? <span className="rr-hero-caption">{t('summaryBreakEven')}</span> : null}
            </div>
          </div>
        )}

        <div className="rr-rows" onScroll={rearm}>
          {rows.map((row) => (
            <div key={row.id} className="rr-row" data-result={row.result}>
              <span className={`rr-row-mark ${row.result}`} aria-hidden="true">
                {row.result === 'win' ? <Check size={14} /> : row.result === 'lose' ? <X size={14} /> : <History size={14} />}
              </span>
              <span className="rr-row-pick">
                <em>{t(gameNameKey(row.game as GameCode))}</em>
                {pickLabel(row.game, row.tab, row.pick, t)}
              </span>
              {row.result === 'pending'
                ? <span className="rr-settling-pill">{t('summarySettling')}</span>
                : <span className={`rr-row-net ${row.result}`}>{amountText(row.net, row.currency)}</span>}
            </div>
          ))}
        </div>

        {settled && view.hiddenRows > 0 && !expanded ? (
          <button className="rr-more" type="button" onClick={() => { setExpanded(true); rearm(); }}>
            {t('summaryShowMore').replace('%n%', String(view.hiddenRows))}
          </button>
        ) : null}
        {settled && expanded && bets.length > 5 ? (
          <button className="rr-more" type="button" onClick={() => { setExpanded(false); rearm(); }}>{t('summaryShowLess')}</button>
        ) : null}

        {settled && layout === 'loss' && !view.breakEven ? (
          <div className="rr-net-line">
            {view.nets.map((item) => t('summaryNetLine').replace('%amount%', amountText(item.net, item.currency))).join(' · ')}
          </div>
        ) : null}

        {view.others.length ? (
          <div className="rr-others">
            {view.others.map((item) => (
              <span key={item.game} className="rr-other">
                <GameIcon game={item.game} className="rr-other-ico" />
                <b>{heroValue(item)}</b>
                {item.tags.map((tag, index) => <i key={index} className={`rr-tag${'key' in tag ? ` tag-${tag.key}` : ''}`}>{tagLabel(tag, t)}</i>)}
              </span>
            ))}
          </div>
        ) : null}

        <footer className="rr-foot">
          <button className="rr-history" type="button" onClick={onHistory}>
            <History size={15} aria-hidden="true" />
            {t('summaryHistory')}
          </button>
          <button className="rr-close" type="button" onClick={onClose} data-round-result-close="true">
            {t('summaryClose')}
          </button>
        </footer>
      </section>
      {showBurst ? <CelebrationBurst tier={view.celebration === 'mega' ? 'mega' : 'win'} onDone={() => setBurstDone(true)} /> : null}
    </div>
  );
}
